import React from "react";
import MyPagination from "components/ui/MyPagination";
import MyRefetchButton from "components/ui/MyRefetchButton";
import Selector from "components/ui/Selector";
import { useForm } from "./TemplatesTab";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { X } from "lucide-react";
import { CreateSpineTemplateButton } from "./CreateSpineTemplateButton";

const STATE_OPTIONS = [
  { code: 0, name: "Draft" },
  { code: 200, name: "InReview" },
  { code: 300, name: "Reject" },
  { code: 100, name: "Scheduled" },
  { code: 401, name: "Online" },
  { code: 600, name: "Offline" },
];

const TYPE_OPTIONS = [
  { code: "character", name: "character" },
  { code: "bg", name: "bg" },
  { code: "fg", name: "fg" },
  { code: "bgEffect", name: "bgEffect" },
  { code: "fgEffect", name: "fgEffect" },
  { code: "bgStatic", name: "bgStatic" },
  { code: "fgStatic", name: "fgStatic" },
];

const DataTableToolbar = ({
  mutate,
  nextAfter,
}: {
  mutate: () => void;
  nextAfter?: string;
}) => {
  const afters = useForm((s) => s.afters);
  const after = useForm((s) => s.after);
  const goAfter = useForm((s) => s.goAfter);
  const tid = useForm((s) => s.tid);
  const setTid = useForm((s) => s.setTid);
  const status = useForm((s) => s.status);
  const setStatus = useForm((s) => s.setStatus);
  const type = useForm((s) => s.type);
  const setType = useForm((s) => s.setType);
  const includeChildren = useForm((s) => s.includeChildren);
  const setIncludeChildren = useForm((s) => s.setIncludeChildren);

  const handleTidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTid(e.target.value);
  };

  const handleClearTid = () => {
    setTid("");
  };

  const hasStatus = status !== null && status !== undefined && status !== "";
  const hasType = type !== null && type !== undefined && type !== "";

  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-4">
        <MyPagination
          nextAfter={nextAfter}
          after={after}
          goAfter={goAfter}
          afters={afters}
        />
        <MyRefetchButton onClick={mutate} />
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="tid-filter" className="text-sm font-medium">
              TID:
            </label>
            <div className="relative">
              <Input
                id="tid-filter"
                type="text"
                placeholder="TID"
                value={tid}
                onChange={handleTidChange}
                className="w-48"
              />
              {tid && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearTid}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="status-filter" className="text-sm font-medium">
              State:
            </label>
            <div className="relative">
              <Selector
                id="status-filter"
                datas={STATE_OPTIONS}
                labelName="State"
                value={hasStatus ? status : null}
                update={setStatus}
                placeholder="All"
              />
              {hasStatus ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  title="Clear state"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setStatus(null);
                  }}
                  className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="type-filter" className="text-sm font-medium">
              Type:
            </label>
            <div className="relative">
              <Selector
                id="type-filter"
                datas={TYPE_OPTIONS}
                labelName="Type"
                value={hasType ? type : null}
                update={(value) => setType(value == null ? null : String(value))}
                placeholder="All"
              />
              {hasType ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  title="Clear type"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setType(null);
                  }}
                  className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="include-children-filter"
              checked={includeChildren}
              onCheckedChange={(checked) =>
                setIncludeChildren(checked === true)
              }
            />
            <label
              htmlFor="include-children-filter"
              className="text-sm font-medium leading-none cursor-pointer whitespace-nowrap"
            >
              包含子模版
            </label>
          </div>
        </div>
      </div>
      <CreateSpineTemplateButton onSuccess={mutate} />
    </div>
  );
};

export default DataTableToolbar;
